import React, { useState, useEffect, useRef, memo } from 'react';
import { getMediaFilePath, getMediaFilePathMenuV2 } from 'backend/additionalInformation';
import { RequestEntity, TPhotoSize, TLoaderType } from 'types';
import { useBlobDownload } from 'hooks/useBlobDownload';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import breadcrumbs from 'images/breadcrumbs.svg';
// import { createAppError } from 'utils/helpers';
import style from './ImgUploader.module.scss';

interface IImgUploaderByEntityId {
	resource: RequestEntity;
	entityID: string; // if "" will show noImageIcon
	photoSize: TPhotoSize;
	isMakeImageRequest?: boolean;
	loaderSize: TLoaderType;
	noImageIdIcon?: string;
	makeForcedReload?: boolean;
	filePath?: Nullable<string>;
	isMenuV2?: boolean;
	infoContent?: React.ReactNode;
}

const ImgUploaderByEntityId: React.FC<IImgUploaderByEntityId> = ({
	resource,
	entityID,
	photoSize,
	isMakeImageRequest = true,
	loaderSize,
	noImageIdIcon = 'menu',
	makeForcedReload,
	filePath = null,
	isMenuV2 = false,
	infoContent,
}) => {
	const [isShowImage, setIsShowImage] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [actualPath, setActualPath] = useState<string>('');
	const componentMounted = useRef(true);
	const blobDownload = useBlobDownload({ filePath, fileExtension: 'jpg', fileName: 'image' });

	const showError = () => {
		setActualPath('error');
		setIsLoading(false);
	};

	const initialization = async () => {
		try {
			const response = isMenuV2
				? await getMediaFilePathMenuV2({ resource, entityID, size: photoSize })
				: await getMediaFilePath({ resource, entityID, size: photoSize });
			if (componentMounted.current) {
				setActualPath(response.data.preSignedUrl ? response.data.preSignedUrl : '');
			}
		} catch (err) {
			if (componentMounted.current) {
				showError();
				// const error = err as Error;
				// const appError = createAppError(error);
				// addAppError(appError);
			}
		}
	};

	useEffect(() => {
		makeForcedReload !== undefined && initialization();
	}, [makeForcedReload]);

	useEffect(() => {
		if (isShowImage) {
			entityID !== '' ? initialization() : setIsLoading(false);
		}
	}, [isShowImage]);

	// ----- This is to avoid unnecessary photo requests ----- //
	useEffect(() => {
		if (!isShowImage && isMakeImageRequest) {
			setIsShowImage(true);
		}
	}, [isMakeImageRequest]);
	// ----- This is to avoid unnecessary photo requests ----- //

	useEffect(() => {
		return () => {
			componentMounted.current = false;
		};
	}, []);

	return (
		<div className={style.container} data-test="ImgUploaderByEntityId">
			{isLoading ? <LoaderWithProps loaderSize={loaderSize} /> : null}
			{actualPath !== 'error' && actualPath && (
				<div className={style.imgWrapper}>
					{filePath && (
						<div onClick={blobDownload} className={style.downloadOverlay}>
							<svg className={style.downloadIcon}>
								<use xlinkHref={`${breadcrumbs}#downloadArrow`} />
							</svg>
						</div>
					)}
					<img
						className={style.img}
						src={actualPath}
						onLoad={() => setIsLoading(false)}
						onError={showError}
					/>
				</div>
			)}
			{actualPath === '' && (
				<div className={style.noImage}>
					<svg className={style.icon}>
						<use xlinkHref={`${breadcrumbs}#${noImageIdIcon}`} />
					</svg>
				</div>
			)}
			{actualPath === 'error' && (
				<div className={style.noImage}>
					<div className={style.flexColumn}>
						<svg className={style.iconError}>
							<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
						</svg>
						<div>Error image</div>
					</div>
				</div>
			)}
			{infoContent && <div className={style.absoluteContent}>{infoContent}</div>}
		</div>
	);
};

export default memo(ImgUploaderByEntityId);
